import { ComponentProps, RefObject, useCallback, useMemo, useRef } from 'react'

import {
    ColumnDef,
    getCoreRowModel,
    Header,
    Row,
    useReactTable,
} from '@tanstack/react-table'

import { useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { usePaginationConfig } from '@/hooks/usePaginationConfig/usePaginationConfig'

import { ErrorState, SortingOrder, Table } from '../../../../components'

import { ReportsItem } from '../../../../hooks'

import { useReportingTableLabelsMap } from './useReportingTableLabelsMap'
import { getColumns } from './getColumns'
import { TableLoadingOverlay } from '../../../../components/common/Table/TableLoadingOverlay'
import { useAppNavigation } from '../../../../hooks/useAppNavigation/useAppNavigation'
import { useReportsApi } from '@/hooks/useApi/useReportsApi/useReportsApi'
import { ContentPlaceholder } from '@/components/molecules'

export const ReportingTableContainer: React.FC = () => {
    const labelsMap = useReportingTableLabelsMap()
    const navigate = useNavigate()
    const { sortBy, order, page, recordsPerPage } = useSearch({
        from: '/_layout/reporting/',
    })
    const ref = useRef<HTMLDivElement>(null)

    const onTableColumnHeaderClick = useCallback(
        (header: Header<unknown, unknown>) => {
            const columnId = header.id
            const newOrder =
                sortBy !== columnId || order === 'asc' ? 'desc' : 'asc'
            const newSortBy = columnId

            void navigate({
                to: '/reporting',
                from: '/reporting',
                search: (prev) => {
                    const next = {
                        ...prev,
                        sortBy: newSortBy,
                        order: newOrder,
                    }

                    return next
                },
            })
        },
        [navigate, order, sortBy]
    )

    const onPaginationInfoChange = useCallback(
        ({
            page,
            recordsPerPage,
        }: {
            page: number
            recordsPerPage: number
        }) => {
            void navigate({
                to: '/reporting',
                from: '/reporting',
                search: (prev) => {
                    const next = {
                        ...prev,
                        page: page,
                        recordsPerPage: recordsPerPage,
                    }

                    return next
                },
            })
        },
        [navigate]
    )

    const getColumnSorting = useCallback(
        (header: Header<unknown, unknown>) => {
            if (sortBy === header.id) {
                return order as SortingOrder
            }
            return undefined
        },
        [order, sortBy]
    )

    const { useGetReports } = useReportsApi()
    const { data, isSuccess, isLoading, isFetching, isError } = useGetReports({
        sortBy,
        order,
        offset: (page - 1) * recordsPerPage,
        limit: recordsPerPage,
    })

    const columns = useMemo(() => getColumns(labelsMap, ref), [labelsMap, ref])

    const pagination = usePaginationConfig({
        totalItems: data?.totalItems || 0,
        onPageChange: (newPage) => {
            onPaginationInfoChange({
                page: newPage,
                recordsPerPage: Number(pagination.recordsPerPage),
            })
        },
        onRecordsPerPageChange: (newRecordsPerPage) => {
            onPaginationInfoChange({
                page: 1,
                recordsPerPage: Number(newRecordsPerPage),
            })
        },
        siblingCount: 2,
        currentPage: page,
        recordsPerPage,
    })

    const emptyData = Array.from(new Array(50)).map(() => {
        const emptyReport = {}

        return emptyReport
    })

    const isLoadingState = isLoading || isFetching

    if (!isLoadingState) {
        if (isSuccess && data.items.length === 0) {
            return <ReportingEmptyState />
        }

        if (isError) {
            return <ReportingErrorState />
        }
    }

    return (
        <ReportingTable
            columns={columns}
            onTableColumnHeaderClick={onTableColumnHeaderClick}
            getColumnSorting={getColumnSorting}
            data={data?.items ?? emptyData}
            isLoading={isLoadingState}
            pagination={pagination}
            tableWrapperRef={ref}
        />
    )
}

const ReportingErrorState = () => {
    const { t } = useTranslation('translations')

    return (
        <ErrorState
            description={t(
                'billing_and_payments.payables_summary.report_error_state.description'
            )}
            className="h-full"
        />
    )
}

const ReportingEmptyState = () => {
    const { t } = useTranslation('translations')
    const title = t('reporting_table.empty_state.title')
    const description = t('reporting_table.empty_state.description')

    return (
        <ContentPlaceholder
            className="h-full"
            illustrationSrc="/assets/no-reports-illustration.svg"
            title={title}
            description={description}
            name="empty-state"
        />
    )
}

type ReportingTableProps = {
    data: ReportsItem[] | Record<never, unknown>[]
    columns: ColumnDef<ReportsItem>[]
    onTableColumnHeaderClick: ComponentProps<
        typeof Table
    >['onTableColumnHeaderClick']
    getColumnSorting: ComponentProps<typeof Table>['getColumnSorting']
    isLoading: boolean
    pagination: ComponentProps<typeof Table>['pagination']
    containerRef?: RefObject<HTMLDivElement>
    tableBodyRef?: RefObject<HTMLTableSectionElement>
    tableWrapperRef?: RefObject<HTMLDivElement>
    tableRef?: RefObject<HTMLTableElement>
}

export const ReportingTable: React.FC<ReportingTableProps> = (props) => {
    const {
        data,
        columns,
        onTableColumnHeaderClick,
        getColumnSorting,
        isLoading,
        pagination,
        containerRef,
        tableBodyRef,
        tableWrapperRef,
        tableRef,
    } = props

    const { goToReport } = useAppNavigation()

    const table = useReactTable({
        data: data,
        columns: columns as ColumnDef<unknown>[],
        getCoreRowModel: getCoreRowModel(),
    })

    const { t } = useTranslation('translations')
    const loadingReportTitle = t('common.loading_reports')

    const onRowClick = (row: Row<unknown>) => {
        void goToReport((row as Row<ReportsItem>).original.customReportId)
    }

    return (
        <div className="relative h-full" ref={containerRef}>
            <Table
                table={table}
                onTableColumnHeaderClick={onTableColumnHeaderClick}
                getColumnSorting={getColumnSorting}
                pagination={pagination}
                style="list"
                onRowClick={onRowClick}
                tableWrapperRef={tableWrapperRef}
                tableBodyRef={tableBodyRef}
                tableRef={tableRef}
                cellClassName="first:max-w-0"
            />

            <TableLoadingOverlay show={isLoading} title={loadingReportTitle} />
        </div>
    )
}
